import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Component, NgModule, inject, makeEnvironmentProviders } from '@angular/core';
import { WebStorageStateStore, Log, UserManager, User } from 'oidc-client';
export { User } from 'oidc-client';
import { BehaviorSubject, ReplaySubject, from, Observable, of, Subject } from 'rxjs';
import { mergeMap, map, takeUntil } from 'rxjs/operators';
import KJUR from 'jsrsasign';
import * as i2 from '@angular/router';

const configurationSub = new BehaviorSubject("");
const AUTH_CONFIG = new InjectionToken("AUTH_CONFIG", { factory: resolveAuthConfig });
function resolveAuthConfig() {
    return configurationSub.value;
}
const SessionAuthorityName = "oidc-authority";
const SessionClientIdName = "oidc-client_id";

class OAuthStorage {
    constructor(config) {
        this.prefix = "";
        this.lifetime = "30";
        this.prefix = config.prefix ? config.prefix : "upmc.";
        this.lifetime = config.cookieLifetime ? config.cookieLifetime : "30";
    }
    clear() {
        let cookies = document.cookie.split(";");
        cookies = cookies.filter(value => {
            return value.startsWith(this.prefix) || value.startsWith(` ${this.prefix}`);
        });
        for (let i = 0; i < cookies.length; i++) {
            let key = cookies[i].split("=");
            document.cookie = key[0] + " =; expires = Thu, 01 Jan 1970 00:00:00 UTC";
        }
    }
    setItem(key, value) {
        this.setItemCookie(key, value, parseFloat(this.lifetime));
    }
    key(index) {
        return this[index];
    }
    getItem(key) {
        const item = this.getItemCookie(key);
        return item == "" ? null : item;
    }
    removeItem(key) {
        this.removeCookie(key);
    }
    removeCookie(cName) {
        const date = new Date();
        date.setTime(date.getTime() - (1000 * 60 * 60 * 24));
        window.document.cookie = cName + "=; expires=" + date.toUTCString();
    }
    getItemCookie(cName) {
        var name = cName + "=";
        var ca = document.cookie.split(';');
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    }
    setItemCookie(cName, cValue, expires) {
        var cookieStr = cName + "=" + cValue + ";";
        if (expires) {
            var expireTime = this.setExpiration(expires);
            cookieStr += "expires=" + expireTime + ";";
        }
        document.cookie = cookieStr;
    }
    setExpiration(cookieLife) {
        var today = new Date();
        var expr = new Date(today.getTime() + cookieLife * 60 * 1000);
        return expr.toUTCString();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OAuthStorage, deps: [{ token: AUTH_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OAuthStorage }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OAuthStorage, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }]; } });

class StorageCache extends WebStorageStateStore {
    constructor(config, internalStorage) {
        super({ prefix: config["prefix"] ? config["prefix"] : "upmc.", store: internalStorage });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StorageCache, deps: [{ token: AUTH_CONFIG }, { token: OAuthStorage }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StorageCache }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: StorageCache, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: OAuthStorage }]; } });

class OidcClientService {
    constructor(config, storage) {
        this.config = config;
        this.storage = storage;
        this.userSubject = new ReplaySubject(1);
        this.sessionVar = "";
        this.config["attachLogger"] ? Log.logger = console : false;
        switch (this.config["logLevel"]) {
            case "DEBUG":
                Log.level = Log.DEBUG;
                break;
            case "WARN":
                Log.level = Log.WARN;
                break;
            case "INFO":
                Log.level = Log.INFO;
                break;
            case "ERROR":
                Log.level = Log.ERROR;
                break;
            case "NONE":
                Log.level = Log.NONE;
                break;
            default:
                break;
        }
        this.config["userStore"] = this.config["userStore"] ? this.config["userStore"] : new WebStorageStateStore({ prefix: config["prefix"] ? config["prefix"] : "upmc.", store: sessionStorage });
        this.config["stateStore"] = this.config["stateStore"] ? this.config["stateStore"] : new WebStorageStateStore({ prefix: config["prefix"] ? config["prefix"] : "upmc.", store: sessionStorage });
        const updatedConfig = Object.create(this.config);
        updatedConfig["userStore"] = storage;
        updatedConfig["stateStore"] = storage;
        this.cookieStorage = updatedConfig;
        this.sessionVar = this.config.prefix ? this.config.prefix + ":useSession" : "useSession";
        this.sessionState = new BehaviorSubject(sessionStorage.getItem(this.sessionVar));
        const rightConfig = this.sessionState.getValue() === "true" ? this.config : updatedConfig;
        this.userManager = new UserManager(rightConfig);
        if (this.inIframe()) {
            this.userManager.signinSilentCallback().then(this.user = null);
        }
        this.addUserLoadedEventHandler();
        //if using sessionStorage then assume calling get user retrieves the user.
        //if in IFrame, don't call getUser. The correct user wouldn't be returned
        //since the token will be in the process of renewing
        if (sessionStorage.getItem(this.sessionVar) === "true" && !this.inIframe()) {
            this.userManager.getUser().then(user => {
                this.user = user;
                this.userSubject.next(user);
            });
        }
    }
    userObservable() {
        return this.userSubject.asObservable();
    }
    /**
     * @returns the current user and its claims
     */
    getUser() {
        return this.user;
    }
    /**
    * @returns the current user and its claims inside of an observable
    */
    getUserComputed() {
        return from(this.userManager.getUser());
    }
    /**
     * Redirect to B2C Sign In. Switches to cookie storage if
     * using session storage
     */
    signIn(args = {}) {
        if (this.sessionState.getValue() === "true") {
            this.switchToCookieStorage();
        }
        return from(this.userManager.signinRedirect(args));
    }
    signInPopUp(args = {}) {
        return new Observable(observer => {
            var signinObs = from(this.userManager.signinPopup(args));
            const signinSub = signinObs.subscribe((user) => {
                let addUserLoadedEventHandler = false;
                if (sessionStorage.getItem(this.sessionVar) !== "true") {
                    sessionStorage.setItem(this.sessionVar, "true");
                    this.sessionState.next("true");
                    addUserLoadedEventHandler = true;
                    // gotta make sure to clean up after ourselves
                    new OAuthStorage({ "prefix": this.getSettings().prefix }).clear();
                    this.userManager = new UserManager(this.config);
                }
                this.userManager.storeUser(user).then(() => {
                    if (addUserLoadedEventHandler) {
                        this.addUserLoadedEventHandler();
                    }
                    observer.next(user);
                    signinSub.unsubscribe();
                    observer.complete();
                }).catch(error => observer.error(error));
            });
        });
    }
    /**
     * Authenticates and validates token after coming back from B2C.
     * This method must be called to complete the authentication process
     * after coming back from B2C. This will also clear the hash,
     * remove the associated cookies, signal to switch to sessionStorage,
     * switch to sessionStorage for storage, and store the user.
     * @param url Optional url to process
     * @returns Observable<User> the user after being successfully stored or error
     */
    completeSignIn(url) {
        return new Observable(observer => {
            this.userManager.signinRedirectCallback(url).then(user => {
                this.stripParams();
                sessionStorage.setItem(this.sessionVar, "true");
                this.sessionState.next("true");
                new OAuthStorage({ "prefix": this.config["prefix"] }).clear();
                this.userManager = new UserManager(this.config);
                this.userManager.storeUser(user).then(() => {
                    this.addUserLoadedEventHandler();
                    observer.next(user);
                    observer.complete();
                }).catch(error => observer.error(error));
            }).catch(error => observer.error(error));
        });
    }
    stripParams() {
        switch (this.config["response_mode"]) {
            case "query":
                window.location.href = window.location.href.split("?")[0];
                break;
            case "fragment":
            default:
                window.location.hash = "";
                break;
        }
    }
    /**
     * @deprecated
     * Passes the stored user object into the user observable.
     */
    refreshUserSubject() {
        this.userSubject.next(this.user);
    }
    /**
     * Removes all cookies currently stored under
     * the configured prefix.
     */
    removeAllCookies() {
        new OAuthStorage({ "prefix": this.config["prefix"] }).clear();
    }
    /**
     * Switches storage to cookies.
     */
    switchToCookieStorage() {
        this.userManager = new UserManager(this.cookieStorage);
        this.sessionState.next(null);
        sessionStorage.removeItem(this.sessionVar);
    }
    /**
     * Silently refresh or acquire a token.
     * @param args arguments to pass to the sign in request
     * @returns an observable with the user after completed refresh or error
     */
    renewToken(args = {}) {
        return new Observable(observer => {
            this.userManager.signinSilent(args).then(user => {
                this.user = user;
                observer.next(user);
                observer.complete();
            }).catch(error => observer.error(error));
        });
    }
    /**
     * Redirect to B2C logout
     */
    signOut(args = {}) {
        sessionStorage.removeItem(this.sessionVar);
        return from(this.userManager.signoutRedirect(args).then(() => {
        }));
    }
    /**
     * Determines if the token is close to expiring by
     * comparing the configured clockskew to the token's exp claim
     * @returns boolean
     */
    isExpiring() {
        return this.isExpiringWithSkew(this.user.profile["exp"]);
    }
    /**
    * Determines if the token is close to expiring by
    * comparing the configured clockskew to the token's exp claim
    * @returns Observable<boolean>
    */
    isExpiringComputed() {
        if (this.user) {
            return new Observable(observer => {
                observer.next(this.isExpiring());
                observer.complete();
            });
        }
        else {
            return new Observable(observer => {
                this.userManager.getUser().then(user => {
                    if (user) {
                        const expiring = this.isExpiringWithSkew(user.profile["exp"]);
                        if (user.profile["exp"]) {
                            observer.next(expiring);
                            observer.complete();
                        }
                        else {
                            observer.error({ error: "No exp claim" });
                        }
                    }
                    else {
                        observer.error({ error: "No user object returned" });
                    }
                }, error => {
                    observer.error(error);
                });
            });
        }
    }
    isExpiringWithSkew(claim) {
        const skew = this.config["clockSkew"] || 0;
        return claim <= this.timeNow() + skew;
    }
    /**
    * Determines if the token has expired by
    * comparing the current time to the token's exp claim
    * @returns boolean
    */
    isExpired() {
        return this.isExpiredInternal(this.user.profile["exp"]);
    }
    /**
    * Determines if the token has expired by
    * comparing the current time to the token's exp claim
    * @returns Observable<boolean>
    */
    isExpiredComputed() {
        if (this.user) {
            return new Observable(observer => {
                observer.next(this.isExpired());
                observer.complete();
            });
        }
        else {
            return new Observable(observer => {
                this.userManager.getUser().then(user => {
                    const res = this.isExpiredInternal(user.profile["exp"]);
                    observer.next(res);
                    observer.complete();
                });
            });
        }
    }
    isExpiredInternal(claim) {
        return claim <= this.timeNow();
    }
    /**
     * Determine if in an iFrame
     * @returns boolean
     */
    inIframe() {
        try {
            return window.self !== window.top;
        }
        catch (e) {
            return true;
        }
    }
    /**
     * Builds a sign in request off of an existing id_token
     * @param id_token The token to create a sign in request from
     * @param completeSignIn additionally call completeSignIn or not
     * @returns an Observable of either state or the signed in user when completeSignIn is set to true
     */
    createSignInFromIdToken(id_token, completeSignIn = false) {
        return new Observable(observer => {
            const token = KJUR.jws.JWS.parse(id_token);
            this.userManager.createSigninRequest().then(request => {
                const state = request.state.id;
                const authority = request.state.authority;
                const client_id = request.state.client_id;
                const redirect_uri = request.state.redirect_uri;
                const newStorageObject = JSON.stringify({
                    id: state,
                    created: token.payloadObj.iat,
                    nonce: token.payloadObj.nonce,
                    redirect_uri: redirect_uri,
                    authority: authority,
                    client_id: client_id
                });
                this.userManager.settings.stateStore.set(state, newStorageObject);
                if (completeSignIn) {
                    const url = `${window.location.href}&state=${state}`;
                    this.completeSignIn(url).subscribe({
                        next: user => {
                            observer.next(user);
                            observer.complete();
                        },
                        error: error => observer.error(error)
                    });
                }
                else {
                    observer.next(state);
                    observer.complete();
                }
            }).catch(error => observer.error(error));
        });
    }
    /**
     * Swaps in new policy url and returns the one swapped out.
     * @param regex string pattern or regex to match current policy
     * @param authority name of new policy to set
     * @returns the previous policy
     */
    setPolicy(regex, policy) {
        const matches = this.userManager.settings.authority.match(regex);
        const currentPolicy = matches.length > 1 ? matches[1] : matches[0];
        const newAuthority = this.userManager.settings.authority.replace(currentPolicy, policy);
        this.setAuthority(newAuthority);
        return currentPolicy;
    }
    /**
     * Swaps and returns the current authority url.
     * @param authority the url to use as the authority
     * @returns the previous authority url
     */
    setAuthority(authority) {
        const previousAuthority = this.userManager.settings.authority;
        this.userManager.settings['_authority'] = authority;
        this.config.authority = this.userManager.settings.authority;
        return previousAuthority;
    }
    /**
     * Swaps and returns the current client id.
     * @param clientId string the client id to use
     * @returns the previous client id
     */
    setClientId(clientId) {
        const previousClientId = this.userManager.settings.client_id;
        this.userManager.settings['_client_id'] = clientId;
        this.config.client_id = clientId;
        return previousClientId;
    }
    /**
     * Returns the current settings for the service. Modification of the returned
     * object will not update the current settings.
     */
    getSettings() {
        return Object.create(this.config);
    }
    /**
     * Overrides the settings in the service. This will cause the stored
     * user to be reloaded in case the storage location changes
     * @param settings the settings to use in the service
     */
    setSettings(settings) {
        this.config = settings;
        this.userManager.removeUser().then();
        this.userManager = new UserManager(this.config);
        this.userManager.storeUser(this.user).then(() => {
            this.addUserLoadedEventHandler();
        });
    }
    /**
     * Decrypts the given token into header, payload, and hexadecimal string of signature.
     * @param str a JWS signature
     */
    decrypt(str) {
        return KJUR.jws.JWS.parse(str);
    }
    // historically, the userSignedIn() method was first written in provider directory because it was headless. No client id and authority url
    /**
     * Look for the signed in user and properly store them if found.
     * This is a HEAVY duty method to call to find out if the user is signed in or not. Only apps
     * who do not have an assigned authority url need to call this method.
     * @returns true for signed in, otherwise false
     */
    userSignedIn() {
        return this.getUserComputed().pipe(mergeMap((user) => {
            if (user) {
                if (!this.userManager.settings.authority) {
                    return from(this.userManager.removeUser()).pipe(map(_ => {
                        this.setupUserStore(user.profile.iss, user.profile.acr, user.profile.aud);
                        return true;
                    }));
                }
                else {
                    return of(true);
                }
            }
            else {
                return from(this.userManager.settings.userStore.getAllKeys()).pipe(mergeMap((keys) => {
                    if (keys.length === 0) {
                        return of(false);
                    }
                    else {
                        return from(this.userManager.settings.userStore.get(keys[0])).pipe(mergeMap((userString) => {
                            const storedUser = User.fromStorageString(userString);
                            if (!this.userManager.settings.authority) {
                                this.setupUserStore(storedUser.profile.iss, storedUser.profile.acr, storedUser.profile.aud);
                            }
                            return from(this.userManager.storeUser(storedUser)).pipe(map(() => {
                                this.userManager.events.load(storedUser);
                                return true;
                            }));
                        }));
                    }
                }));
            }
        }));
    }
    /**
     * Overrides the clientId and authority url. Authority url will be of the form "{issuer}.well-known/openid-configuration?p={acr}"
     * This will also store the authority url and client_id into session storage.
     * If you require your authority url to not have the ".well-known/open-id-configuration" section then you will need to call
     * setClientId, setSessionClientId, setAuthority, and setSessionAuthority yourself.
     * @param issuer issuer claim
     * @param acr acr claim
     * @param audience aud claim
     */
    setupUserStore(issuer, acr, audience) {
        this.setClientId(audience);
        this.setSessionClientId(audience);
        const authority = issuer + '.well-known/openid-configuration?p=' + acr;
        this.setAuthority(authority);
        this.setSessionAuthority(authority);
    }
    /**
     * Gets the authority url stored in session storage.
     * @returns the stored authority url
     */
    getSessionAuthority() {
        return sessionStorage.getItem(SessionAuthorityName);
    }
    /**
     * Stores the given url in session storage.
     * @param authority the url to store
     */
    setSessionAuthority(authority) {
        sessionStorage.setItem(SessionAuthorityName, authority);
    }
    /**
     * Gets the client id stored in session storage.
     * @returns the stored client id
     */
    getSessionClientId() {
        return sessionStorage.getItem(SessionClientIdName);
    }
    /**
     * Stores the given client id in session storage.
     * @param clientId the client id to store
     */
    setSessionClientId(clientId) {
        sessionStorage.setItem(SessionClientIdName, clientId);
    }
    timeNow() {
        return Math.round(Date.now() / 1e3);
    }
    addUserLoadedEventHandler() {
        this.userManager.events.addUserLoaded(user => {
            this.user = user;
            this.userSubject.next(user);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OidcClientService, deps: [{ token: AUTH_CONFIG }, { token: StorageCache }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OidcClientService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: OidcClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AUTH_CONFIG]
                }] }, { type: StorageCache }]; } });

class SsoLoginGateComponent {
    constructor(oidcClient, route, router) {
        this.oidcClient = oidcClient;
        this.route = route;
        this.router = router;
        this.subject = new Subject();
    }
    ngOnInit() {
        this.route.data.pipe(takeUntil(this.subject)).subscribe({
            next: ({ allowedEnvs, redirectRoute, claim }) => {
                this.allowedEnvs = allowedEnvs;
                this.redirectRoute = redirectRoute;
                this.claim = claim;
            }
        });
    }
    ngAfterViewInit() {
        this.route.fragment.pipe(takeUntil(this.subject)).subscribe(fragment => {
            if (fragment == null) {
                this.subject.next(true);
                this.router.navigate([this.redirectRoute]);
            }
            else {
                var idToken = fragment.split('&')[0].split('=')[1];
                var token = this.oidcClient.decrypt(idToken);
                const envClaim = token.payloadObj[this.claim];
                if (this.allowedEnvs.includes(envClaim)) {
                    this.oidcClient.setClientId(token.payloadObj.aud);
                    const sub2 = this.oidcClient.createSignInFromIdToken(idToken, true).subscribe(x => {
                        sub2.unsubscribe();
                    });
                }
                else {
                    this.subject.next(true);
                    this.router.navigate([this.redirectRoute]);
                }
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoLoginGateComponent, deps: [{ token: OidcClientService }, { token: i2.ActivatedRoute }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SsoLoginGateComponent, isStandalone: true, selector: "oidc-sso-login", ngImport: i0, template: 'Processing token...', isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoLoginGateComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'oidc-sso-login',
                    template: 'Processing token...',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: OidcClientService }, { type: i2.ActivatedRoute }, { type: i2.Router }]; } });

class HpOidcClientModule {
    static forPlatform(authConfig) {
        return [
            { provide: AUTH_CONFIG, useFactory: authConfig },
            { provide: StorageCache, useClass: StorageCache },
            { provide: OAuthStorage, useClass: OAuthStorage },
            { provide: OidcClientService, useClass: OidcClientService }
        ];
    }
    static forRoot(authConfig) {
        return {
            ngModule: HpOidcClientModule,
            providers: [
                { provide: AUTH_CONFIG, useFactory: authConfig },
                StorageCache,
                OAuthStorage,
                OidcClientService
            ]
        };
    }
    static forChild(authConfig) {
        return {
            ngModule: HpOidcClientModule,
            providers: [
                { provide: AUTH_CONFIG, useFactory: authConfig },
                StorageCache,
                OAuthStorage,
                OidcClientService
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HpOidcClientModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: HpOidcClientModule, imports: [SsoLoginGateComponent], exports: [SsoLoginGateComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HpOidcClientModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: HpOidcClientModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [SsoLoginGateComponent],
                    exports: [
                        SsoLoginGateComponent
                    ]
                }]
        }] });

/**
 * The purpose of this guard is to act as an all purpose redirect
 * from here to b2c. The following values can be used as a query parameter.
 * The acceptance of those values are based on the forwarded b2c policy
 * response_type, scope, redirect_uri,
 * data was meant to be the place a caller could indicate the data to
 * have round tripped, but people were getting confused, so state has been added (since that matches the spec)
 * and so now if data is not passed, but state is then state will be used
 * data, state, prompt, display, max_age, ui_locales, id_token_hint, login_hint, acr_values,
 * resource, request, request_uri, response_mode, extraQueryParams
 *
 * @Optional an implementation of {@link ARGQueryParamService}
 *
 * @publicApi
 */
const autoRedirectGuard = ({ queryParams }) => {
    let auth = inject(OidcClientService);
    let argqpService = inject(ARGQueryParamService, { optional: true });
    queryParams = argqpService?.process(queryParams) ?? queryParams;
    auth.signIn(queryParams);
    return false;
};
/**
 * Gives developers the chance to do something with the query parameters before
 * going off to B2C. The params object is non-extensible so be mindful
 * if you replace the entire object with something different.
 */
class ARGQueryParamService {
}

/**
 * @description
 * Provides the `AUTH_CONFIG` token with the given config.
 *
 * @param config The config to configure oidc-client.
 * @returns The environment providers.
 *
 * @example
 * ```ts
 * import { provideOidcClient } from '@upmc-healthplan-npm/oidc-client';
 *
 * bootstrap(AppComponent, {
 *   providers: [
 *     provideOidcClient()
 *   ],
 * })
 * ```
 */
const provideOidcClient = (config) => {
    return makeEnvironmentProviders(provideOidcClientPlatform(config));
};
/**
 * @description
 * Provides the `AUTH_CONFIG` token with the given config.
 *
 * @param config The config to configure oidc-client
 * @returns The static providers.
 *
 *  * @example
 * ```ts
 * import { provideOidcClientPlatform } from '@upmc-healthplan-npm/oidc-client';
 *
 * platformBrowserDynamic(provideOidcClientPlatform({}));
 * ```
 */
const provideOidcClientPlatform = (config) => {
    return [
        {
            provide: AUTH_CONFIG,
            useFactory: typeof config === 'function' ? config : () => config
        },
        { provide: StorageCache, useClass: StorageCache },
        { provide: OAuthStorage, useClass: OAuthStorage },
        { provide: OidcClientService, useClass: OidcClientService }
    ];
};

/*
 * Public API Surface of hp-oidc-client
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ARGQueryParamService, HpOidcClientModule, OAuthStorage, OidcClientService, SsoLoginGateComponent, StorageCache, autoRedirectGuard, provideOidcClient, provideOidcClientPlatform };
